import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Button,
    Collapse
} from '@mui/material';
import {
    Receipt as InvoiceIcon,
    Today as TodayIcon,
    CalendarMonth as MonthIcon,
    Warning as OverdueIcon,
    Paid as PaidIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import InvoiceList from './invoices/InvoiceList';
import { invoiceService } from '../../services/invoiceService';
import authService from '../../services/authService';
import { formatAmount } from '../../utils/formatters';

const InvoiceManagement = () => {
    const [stats, setStats] = useState(null);
    const [showAllWidgets, setShowAllWidgets] = useState(false);
    const [listFilters, setListFilters] = useState({});
    const { showSuccess, showError } = useApiHandler();

    // Check user permissions
    const canViewRevenue = authService.canViewRevenue();

    // Load persisted filters from localStorage
    const loadPersistedFilters = () => {
        try {
            const saved = localStorage.getItem('invoice_filters_persistence');
            if (saved) {
                const filters = JSON.parse(saved);
                // Convert filter names to match API expectations
                const apiFilters = {};
                if (filters.searchText) apiFilters.search = filters.searchText;
                if (filters.fromDate) apiFilters.from_date = filters.fromDate;
                if (filters.toDate) apiFilters.to_date = filters.toDate;
                if (filters.fromDueDate) apiFilters.from_due_date = filters.fromDueDate;
                if (filters.toDueDate) apiFilters.to_due_date = filters.toDueDate;
                if (filters.selectedCustomer?.id) apiFilters.customer_id = filters.selectedCustomer.id;
                if (filters.statusFilter) apiFilters.status = filters.statusFilter;
                if (filters.invoiceTypeFilter) apiFilters.invoice_type = filters.invoiceTypeFilter;
                return apiFilters;
            }
        } catch (error) {
            console.error('Error loading persisted invoice filters:', error);
        }
        return {};
    };

    useEffect(() => {
        // Load persisted filters and initialize stats
        const persistedFilters = loadPersistedFilters();
        loadInvoiceStats(persistedFilters);
    }, []);

    const loadInvoiceStats = async (filters = {}) => {
        try {
            console.log('Loading invoice stats with filters:', filters);
            const response = await invoiceService.getInvoiceStats(filters);
            if (response.success) {
                console.log('Invoice stats response:', response.data);
                console.log('Amount values:', {
                    total_amount: response.data.totals?.amount,
                    pending_amount: response.data.by_status?.pending?.amount,
                    paid_amount: response.data.by_status?.paid?.amount,
                    overdue_amount: response.data.by_status?.overdue?.amount,
                    cancelled_amount: response.data.by_status?.cancelled?.amount
                });
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            showError('Error loading invoice statistics');
        }
    };

    const refreshStats = (filters = {}) => {
        setListFilters(filters);
        // If no filters provided, use persisted filters
        const filtersToUse = Object.keys(filters).length > 0 ? filters : loadPersistedFilters();
        loadInvoiceStats(filtersToUse);
    };

    const handleViewMoreToggle = () => {
        setShowAllWidgets(!showAllWidgets);
    };

    const formatLakhs = (value) => {
        // Normalize strings like "77,841.32" or "₹77,841.32"
        console.log('formatLakhs input:', value, 'type:', typeof value);
        const num = Number(String(value ?? '0').replace(/[^0-9.\-]/g, ''));
        const safe = Number.isFinite(num) ? num : 0;
        console.log('formatLakhs safe number:', safe);
        
        // If amount is less than 1 lakh, show in thousands
        if (safe < 100000) {
            const result = (safe / 1000).toFixed(1);
            console.log('formatLakhs result (thousands):', result);
            return result + 'K';
        } else {
            const result = (safe / 100000).toFixed(1);
            console.log('formatLakhs result (lakhs):', result);
            return result + 'L';
        }
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Invoice Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Generate, manage and track customer invoices and billing
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <>
                    {/* Header with View More Button */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2 
                    }}>
                        <Typography variant="h6" color="text.secondary">
                            Invoice Statistics
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleViewMoreToggle}
                            startIcon={showAllWidgets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            size="small"
                        >
                            {showAllWidgets ? 'Show Less' : 'View More Stats'}
                        </Button>
                    </Box>

                    <Grid container spacing={3} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <InvoiceIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Total Invoices</Typography>
                                    </Box>
                                    <Typography variant="h4" color="primary" gutterBottom>
                                        {(stats.totals?.invoices ?? stats.total_invoices ?? 0)}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <Chip 
                                            label={stats.filter_info?.is_default_range ? 'Current Month' : (stats.filter_info?.has_filters ? 'Filtered' : 'All Time')} 
                                            color="primary" 
                                            size="small" 
                                        />
                                        {canViewRevenue && (
                                            <Chip label={`₹${formatLakhs(stats.totals?.amount ?? stats.total_amount)}`} color="primary" size="small" />
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <TodayIcon color="warning" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Pending</Typography>
                                    </Box>
                                    <Typography variant="h4" color="warning.main" gutterBottom>
                                        {(stats.by_status?.pending?.count ?? stats.pending_invoices ?? 0)}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <Chip 
                                            label={stats.filter_info?.is_default_range ? 'Current Month' : (stats.filter_info?.has_filters ? 'Filtered' : 'All Time')} 
                                            color="warning" 
                                            size="small" 
                                        />
                                        {canViewRevenue && (
                                            <Chip label={`₹${formatLakhs(stats.by_status?.pending?.amount ?? stats.pending_amount)}`} color="warning" size="small" />
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <PaidIcon color="success" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Paid</Typography>
                                    </Box>
                                    <Typography variant="h4" color="success.main" gutterBottom>
                                        {(stats.by_status?.paid?.count ?? stats.paid_invoices ?? 0)}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <Chip 
                                            label={stats.filter_info?.is_default_range ? 'Current Month' : (stats.filter_info?.has_filters ? 'Filtered' : 'All Time')} 
                                            color="success" 
                                            size="small" 
                                        />
                                        {canViewRevenue && (
                                            <Chip label={`₹${formatLakhs(stats.by_status?.paid?.amount ?? stats.paid_amount)}`} color="success" size="small" />
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <OverdueIcon color="error" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Overdue</Typography>
                                    </Box>
                                    <Typography variant="h4" color="error.main" gutterBottom>
                                        {(stats.by_status?.overdue?.count ?? stats.overdue_invoices ?? 0)}
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <Chip 
                                            label={stats.filter_info?.is_default_range ? 'Current Month' : (stats.filter_info?.has_filters ? 'Filtered' : 'All Time')} 
                                            color="error" 
                                            size="small" 
                                        />
                                        {canViewRevenue && (
                                            <Chip label={`₹${formatLakhs(stats.by_status?.overdue?.amount ?? stats.overdue_amount)}`} color="error" size="small" />
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Additional Statistics Cards - Collapsible */}
                    <Collapse in={showAllWidgets}>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            {/* Additional widgets can be added here */}
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <MonthIcon color="info" sx={{ mr: 1 }} />
                                            <Typography variant="h6">This Month</Typography>
                                        </Box>
                                        <Typography variant="h4" color="info.main" gutterBottom>
                                            {(stats.by_status?.this_month?.count ?? 0)}
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Chip 
                                                label={stats.filter_info?.is_default_range ? 'Current Month' : (stats.filter_info?.has_filters ? 'Filtered' : 'All Time')} 
                                                color="info" 
                                                size="small" 
                                            />
                                            {canViewRevenue && (
                                                <Chip label={`₹${formatLakhs(stats.by_status?.this_month?.amount ?? 0)}`} color="info" size="small" />
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Collapse>
                </>
            )}

            {/* Invoice Management */}
            <Paper sx={{ width: '100%' }}>
                <InvoiceList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
};

export default InvoiceManagement; 